import { FormLabel, Loading, Modal, Pagination } from "@wfp/ui"
import React, { useCallback, useEffect, useRef, useState } from "react"

import Select from "react-select"
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons"
import Button from "components/Shared/Button/Button"
import { formatDate } from "utils/helpers"
import useScData from "hooks/useScData"
import { GraphLineData, OutliersLimits, ReportTableData, SimpleSelect } from "interfaces/dataInterfaces"

import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import { useNavigate, useParams } from "react-router-dom"
import { useCheckDuplicatesMutation, useGetOutliersByValueQuery } from "services/marketprices/outliers"
import { useGetReportDataQuery, useSaveDataReportMutation } from "services/marketprices/reports"
import { useGetGraphDataMutation } from "services/marketprices/commodityPrice"

import ReportValidation from "../../components/Reports/ReportValidation"
import ReportDataDetails from "../../components/Reports/ReportDataDetails"
import ReportVisualDataReview from "../../components/Reports/ReportVisualDataReview"

interface Props {
  reportData?: { errorList: string[]; warningList: string[]; extendedItems: ReportTableData[] }
  adm0Code?: number
  countryName?: string
  frequencyId?: string
}

export default function ViewReport({ reportData, adm0Code, countryName, frequencyId }: Props) {
  const { reportId } = useParams()
  const navigate = useNavigate()

  const selectRef = useRef<any>(null)

  const { data, isLoading: isReportLoading } = useGetReportDataQuery(+reportId! as any, {
    skip: !reportId,
    refetchOnMountOrArgChange: true,
  })
  const { data: outliers, isLoading: isOutliersLoading } = useGetOutliersByValueQuery({ adm0Code }, { skip: !adm0Code })
  const [checkDuplicates, { isLoading: isValidating }] = useCheckDuplicatesMutation()
  const [saveReport, { isLoading }] = useSaveDataReportMutation()
  const [getGraphData, { data: graphData }] = useGetGraphDataMutation()

  const [excelErrors, setExcelErrors] = useState<boolean>(!!reportData?.errorList)
  const [validationData, setValidationData] = useState<ReportTableData[] | undefined>(
    data?.length ? data : reportData?.extendedItems,
  )
  const [outliersLimit, setOutliersLimit] = useState<OutliersLimits[]>([])
  const [showValidationModal, setShowValidationModal] = useState<boolean>(false)
  const [showDetails, setShowDetails] = useState<ReportTableData | null>(null)
  const [visualData, setVisualData] = useState<{
    report: ReportTableData[]
    selectData: SimpleSelect[]
    series: any
    reportedObs?: ReportTableData
  }>({
    selectData: [],
    report: [],
    series: [],
  })
  const [showVisualData, setShowVisualData] = useState<boolean>(false)
  const [status, setStatus] = useState<any>({ isValidated: false, error: false, duplicated: false })
  const [errors, setErrors] = useState<any>({})
  const [actionItem, setActionItem] = useState<any>(null)
  const [actionUpdatedValues, setActionUpdatedValues] = useState<any>(null)
  const [actionDropdowns, setActionDropdowns] = useState<any>({})
  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(1000)

  const commodityRef = useRef<any>(null)
  const priceTypeRef = useRef<any>(null)
  const currencyRef = useRef<any>(null)
  const unitRef = useRef<any>(null)

  const { marketsDropdown, commoditiesDropdown, unitsDropdown, priceTypesDropdown, currenciesDropdown } = useScData({
    adm0Code: adm0Code || 0,
  })

  const isViewing = useCallback(() => {
    return !reportData && !frequencyId
  }, [reportData, frequencyId])

  const handleValidate = async () => {
    try {
      const validate = validationData
      const series: any[] = []
      const invalidValues: string[] = []
      const outliersErrors: string[] = []
      const newValidationData: ReportTableData[] = []

      validate?.forEach((row, index) => {
        const {
          commodityPriceDate,
          commodityUnitName,
          marketName,
          priceTypeName,
          seriesCombinationCode,
          commodityName,
          commodityPriceValue,
        } = row
        const modifiedRow: ReportTableData = { ...row }

        if (!row.validSeries) {
          modifiedRow.isSeriesInvalid = true
          invalidValues.push(
            `<b>Series ${marketName} - ${commodityName} - ${commodityUnitName} - ${priceTypeName}</b> at row ${
              index + 1
            } does NOT belong to any of valid series for the <b>Country: ${countryName}</b>`,
          )
        }
        if (!row.validDate) {
          modifiedRow.isDateInvalid = true
          invalidValues.push(
            `<b>Date ${formatDate(commodityPriceDate, "DD-MMM-YYYY")} </b> at row ${index + 1} is not valid.</b>`,
          )
        }
        series.push({ commodityPriceDate, seriesCombinationCode })

        const limits = outliersLimit[seriesCombinationCode as any]
        if (limits && (commodityPriceValue > limits?.maxValue || commodityPriceValue < limits?.minValue)) {
          modifiedRow.isPriceInvalid = true
          outliersErrors.push(
            `Series at row ${modifiedRow.id}, <b>${marketName}-${
              row.commodityName
            }-${commodityUnitName}-${priceTypeName}</b> having price value: <b>${commodityPriceValue.toFixed(
              2,
            )}</b> does NOT fall in between <b>Max: ${limits?.maxValue.toFixed(2)} and Min: ${limits?.minValue.toFixed(
              2,
            )}</b>.`,
          )
        }

        newValidationData.push(modifiedRow)
      })

      // validate duplication status
      // let duplicates = duplicatedData
      let duplicates: any = null
      if (frequencyId) {
        duplicates = (await checkDuplicates({ frequencyId, adm0Code: adm0Code || 0, series }).unwrap()) || []
      }
      const duplicatedValues: any[] = []
      duplicates?.forEach((item: ReportTableData) => {
        const index = newValidationData.findIndex(
          row =>
            row.commodityPriceDate.includes(item.commodityPriceDate) &&
            row.seriesCombinationCode === item.seriesCombinationCode &&
            row.marketId === item.marketId,
        )
        if (index !== -1) newValidationData[index].isDuplicated = true
        duplicatedValues.push(
          `Series at row ${newValidationData[index].id}, <b>${newValidationData[index].marketName}-${
            newValidationData[index].commodityName
          }-${newValidationData[index].priceTypeName}-${newValidationData[index].commodityUnitName}</b> (${
            item.seriesCombinationCode
          }), with Date <b>${formatDate(item.commodityPriceDate, "DD-MMM-YYYY")}</b> is already present in the system.`,
        )
      })

      const isInvalidSeries = validationData?.some(item => !item.validSeries || !item.validDate)

      setValidationData(newValidationData)
      setErrors({ invalid: invalidValues, duplicates: duplicatedValues, outliers: outliersErrors }) // set individual errors and verification status
      setStatus({
        isValidated: true,
        error: invalidValues.length > 0 || isInvalidSeries,
        duplicated: duplicatedValues?.length > 0,
      })
      setShowValidationModal(true)
    } catch (error) {
      alert("Invalid file data.")
    }
  }

  const handleSave = () => {
    if (!validationData || !outliers || !reportId) return
    const prices = validationData.map(item => {
      const mappedItem = { ...item, isSeriesValid: !item.isSeriesInvalid }
      delete mappedItem.isDuplicated
      delete mappedItem.isPriceInvalid
      delete mappedItem.isSeriesInvalid
      delete mappedItem.isDateInvalid
      return mappedItem
    })
    saveReport({ prices, outliers, reportId }).then(() => navigate("/market-prices/reports"))
  }

  const handleObsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    lastMatchedIndex: number,
    metaIndex: number,
  ) => {
    const { value } = event.target
    const val = Number(value)
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(val) || !validationData?.length || isNaN(lastMatchedIndex) || isNaN(metaIndex)) return

    const newValidationData: ReportTableData[] = JSON.parse(JSON.stringify(validationData))
    const metaCount = newValidationData[lastMatchedIndex].metadata.length

    newValidationData[lastMatchedIndex].metadata[metaIndex].obsPriceValue = val
    const reportedAvg = newValidationData[lastMatchedIndex]?.metadata?.reduce((t, meta) => t + meta.obsPriceValue, 0)
    newValidationData[lastMatchedIndex].commodityPriceValue = reportedAvg / metaCount
    setValidationData(newValidationData)
  }

  const hasValue = useCallback(ref => ref.current.getValue()?.length, [])

  const handleGetGraphData = (row?: ReportTableData) => {
    row && getGraphData(row)
  }

  const handleEditInvalidSeries = useCallback(() => {
    const indexToChange = validationData?.findIndex(item => item.id === actionItem.id)
    if (indexToChange === undefined || indexToChange === -1 || !validationData) return
    if (!hasValue(commodityRef) || !hasValue(currencyRef) || !hasValue(priceTypeRef) || !hasValue(unitRef)) {
      alert("All fields are required!")
      return
    }

    const newValidationData: ReportTableData[] = JSON.parse(JSON.stringify(validationData))
    const newData = { ...validationData[indexToChange], ...actionUpdatedValues }
    const seriesCombinationCode = `${newData.marketId}-${newData.commodityId}-${newData.priceTypeID}-${newData.commodityUnitId}-${newData.currencyId}`
    const isDuplicated = newValidationData.findIndex(
      row =>
        row.commodityPriceDate.includes(newData.commodityPriceDate) &&
        row.seriesCombinationCode === seriesCombinationCode,
    )
    if (isDuplicated !== -1) {
      alert("Duplicated values are not allowed, please change your selections!")
      return
    }

    newValidationData[indexToChange] = { ...newData, seriesCombinationCode, validSeries: true }
    setValidationData(newValidationData)
    setActionItem(null)
    setStatus({ ...status, isValidated: false })
  }, [actionItem, validationData, actionUpdatedValues])

  const filterDropdownValues = useCallback(
    (selected = {}) => {
      const newDropdowns = { markets: marketsDropdown, ...actionDropdowns }

      if (selected.marketId) {
        newDropdowns.commodities = commoditiesDropdown.filter(({ marketID }) => selected.marketId === marketID)
      }

      if (selected.commodityId) {
        newDropdowns.currencies = currenciesDropdown.filter(({ commodityID }) => selected.commodityId === commodityID)
        newDropdowns.priceTypes = priceTypesDropdown.filter(({ commodityID }) => selected.commodityId === commodityID)
        newDropdowns.units = unitsDropdown.filter(({ commodityID }) => selected.commodityId === commodityID)
      }

      setActionDropdowns(newDropdowns)
    },
    [commoditiesDropdown, currenciesDropdown, priceTypesDropdown, unitsDropdown, actionDropdowns],
  )

  useEffect(() => {
    const mappedOutliers: OutliersLimits[] = []
    if (outliers?.length) {
      outliers.forEach((item: OutliersLimits) => (mappedOutliers[item.seriesCode as any] = item))
      setOutliersLimit(mappedOutliers)
    }
  }, [outliers])

  useEffect(() => {
    if (data?.length) setValidationData(data)
  }, [data])

  useEffect(() => {
    if (!actionItem) return

    filterDropdownValues(actionItem) // filter data on the basis of edit action
  }, [actionItem])

  useEffect(() => {
    if (!actionUpdatedValues) return

    filterDropdownValues(actionUpdatedValues) // filter data on the basis of selected values from dropdown
  }, [actionUpdatedValues])

  useEffect(() => {
    if (!validationData?.length) return

    const report = validationData?.filter(
      (v, i, a) => v.isPriceInvalid && a.findIndex(v2 => v2.seriesCombinationCode === v.seriesCombinationCode) === i,
    ) // filters unique series combination code
    const selectData = report.map(row => ({
      label: `${row?.marketName} ${row.commodityName} ${row.priceTypeName} ${row.commodityUnitName}`,
      value: row.seriesCombinationCode,
    }))

    setVisualData({ ...visualData, report, selectData })
    if (report.length && !selectRef?.current?.getValue()[0]?.value) getGraphData(report[0]) // as first visual data is default selected so pre-load it
    if (report.length && !selectRef?.current?.getValue()[0]?.value) {
      getGraphData(report[0]) // as first visual data is default selected so pre-load it
    }
  }, [validationData])

  useEffect(() => {
    if (!graphData || !validationData?.length) return

    const seriesC = selectRef?.current?.getValue()[0].value
    const lastDataIndex = validationData
      .slice()
      .reverse()
      .findIndex(item => item.seriesCombinationCode === seriesC) // needed last data to calculate conjuction point

    const validationIndex = validationData.length - 1 - lastDataIndex
    const lastData = validationData[validationIndex]
    const filteredData = validationData.find(item => item.seriesCombinationCode === seriesC)

    if (!filteredData) return

    const reportedObs = filteredData.metadata?.map(meta => ({
      x: filteredData.commodityPriceDate,
      y: meta.obsPriceValue,
    }))
    const reportedAvg = reportedObs.reduce((t, item) => t + item.y, 0) / reportedObs.length

    const historicalPriceData = graphData?.historicalPriceData || []

    const formatData = (formData: GraphLineData[] | null) => {
      if (!formData) return []
      return formData.map(item => ({ ...item, x: new Date(item.x).getTime() }))
    }

    if (!lastData) return
    lastData.validationIndex = validationIndex

    const series = [
      { name: "Reported Observation(s)", data: formatData(reportedObs), fill: "orange", stroke: "orange" },
      {
        name: "Historical Observations",
        data: formatData(graphData?.historicalPriceObservations),
        fill: "green",
        stroke: "green",
      },
      { name: "Historical Average", data: formatData(historicalPriceData), fill: "#357CD2", stroke: "#357CD2" },
      {
        name: "Reported Average",
        data: formatData([{ x: filteredData.commodityPriceDate, y: reportedAvg }]),
        fill: "#26bde2",
        stroke: "#26bde2",
      },
    ]

    const filteredSeries = series.filter(item => item.data.length)

    setVisualData({ ...visualData, series: filteredSeries, reportedObs: lastData })
  }, [graphData, validationData])

  const columns: Array<ColumnShape<ReportTableData>> = [
    {
      key: "row",
      title: "Row",
      dataKey: "id",
      width: 60,
    },
    {
      key: "commodityPriceDate",
      title: "Date",
      width: 0,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <span className={`${rowData.isDateInvalid ? "bg-red-500 w-full h-full pl-2 pt-3 text-white" : ""}`}>
          {formatDate(rowData.commodityPriceDate, "DD-MMM-YYYY")}
        </span>
      ),
    },
    {
      key: "marketName",
      dataKey: "marketName",
      title: "Market",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "commodityName",
      dataKey: "commodityName",
      title: "Commodity",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "priceTypeName",
      dataKey: "priceTypeName",
      title: "Price Type",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "commodityUnitName",
      dataKey: "commodityUnitName",
      title: "Unit",
      width: 60,
      // flexGrow: 1,
    },
    {
      key: "commodityPriceValue",
      dataKey: "commodityPriceValue",
      title: "Price",
      width: 200,
      // flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <span className={`${rowData.isPriceInvalid ? "bg-yellow-200 w-full h-full pt-3 pl-2" : ""}`}>
          {rowData.commodityPriceValue}
        </span>
      ),
    },
    {
      key: "commodityPriceSourceName",
      dataKey: "commodityPriceSourceName",
      title: "Source",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "action",
      width: 0,
      flexGrow: 2,
      cellRenderer: ({ rowData }) => (
        <>
          <Button
            action={() => setShowDetails(rowData)}
            label="Details"
            className="btn-full blue action-btn"
            style={{ marginLeft: "10px" }}
            icon={faEye}
          />
          {rowData.validSeries !== undefined && !rowData.validSeries && !isViewing() && (
            <Button
              action={() => setActionItem(rowData)}
              label="Edit"
              className="btn-full blue action-btn"
              style={{ marginLeft: "10px" }}
              icon={faEdit}
            />
          )}
        </>
      ),
    },
  ]

  const rowClassName = useCallback(
    ({ rowData }) => {
      // invalid series then must higlight, or user is uploading data against deleted market which already exist in DB so duplicated and invalid
      if (rowData.validSeries !== undefined && !rowData.validSeries && !isViewing()) return "!bg-red-500"
      if (rowData.isDuplicated) return "!bg-purple-300"
      return "valid"
    },
    [validationData],
  )

  return (
    <div className="mt-1">
      {(isLoading || isValidating || isOutliersLoading || isReportLoading) && (
        <Loading active={isLoading || isValidating || isOutliersLoading || isReportLoading} withOverlay />
      )}
      {!isViewing() && (
        <div className="mb-1 d-flex">
          <p>
            <span className="bg-yellow-200 h-3 w-3 inline-flex mr-1" />
            Potential Outlier
          </p>
          <p>
            <span className="bg-purple-300 h-3 w-3 inline-flex ml-2 mr-1" />
            Duplicated Rows
          </p>
          <p>
            <span className="bg-red-500 h-3 w-3 inline-flex ml-2 mr-1" />
            Invalid Series or Date
          </p>
        </div>
      )}

      <div style={{ height: "100%", minHeight: "60vh" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={validationData
                ?.map(itemData => ({
                  ...itemData,
                  key: `${itemData.commodityPriceDate}-${itemData.seriesCombinationCode}-${itemData.marketId}`,
                }))
                ?.slice((pageNumber - 1) * getItemLimit, (pageNumber - 1) * getItemLimit + getItemLimit)}
              width={width}
              height={height}
              rowKey="key"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              rowClassName={rowClassName}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Pagination
        totalItems={validationData?.length ?? 0}
        pageSize={getItemLimit}
        pageSizes={[1000, 2000, 3000]}
        page={pageNumber}
        onChange={({ page, pageSize }: any): void => {
          setPageNumber(page)
          setGetItemLimit(pageSize)
        }}
      />

      <Modal
        open={excelErrors && !isOutliersLoading}
        modalHeading="Excel Validation"
        secondaryButtonText="Close"
        passiveModal
        onRequestClose={() => setExcelErrors(false)}
      >
        <ReportValidation reportData={reportData} />
      </Modal>

      <Modal
        open={!!showDetails}
        modalHeading="Details"
        secondaryButtonText="Close"
        passiveModal
        onRequestClose={() => setShowDetails(null)}
      >
        <ReportDataDetails showDetails={showDetails} />
      </Modal>

      <Modal
        open={!!actionItem}
        modalHeading="Edit Invalid Series"
        secondaryButtonText="Close"
        primaryButtonText="Save"
        onSecondarySubmit={() => setActionItem(null)}
        onRequestClose={() => setActionItem(null)}
        onRequestSubmit={handleEditInvalidSeries}
      >
        <FormLabel className="d-flex items-center">
          <span className="w-40">Market</span>
          <Select
            isSearchable
            className="ml-2 w-100"
            classNamePrefix="select"
            defaultValue={actionDropdowns?.markets?.find((item: any) => item.value === actionItem?.marketId)}
            options={actionDropdowns.markets}
            onChange={e => {
              commodityRef?.current?.clearValue()
              setActionUpdatedValues({ ...actionUpdatedValues, marketId: e?.value, marketName: e?.label })
            }}
          />
        </FormLabel>
        <FormLabel className="d-flex items-center">
          <span className="w-40">Commodity</span>
          <Select
            isSearchable
            className="ml-2 w-100"
            classNamePrefix="select"
            ref={commodityRef}
            defaultValue={actionDropdowns?.commodities?.find((item: any) => item.value === actionItem?.commodityId)}
            options={actionDropdowns.commodities}
            onChange={e => {
              currencyRef?.current?.clearValue()
              unitRef?.current?.clearValue()
              priceTypeRef?.current?.clearValue()
              setActionUpdatedValues({ ...actionUpdatedValues, commodityId: e?.value, commodityName: e?.label })
            }}
          />
        </FormLabel>
        <FormLabel className="d-flex items-center">
          <span className="w-40">Price Type</span>
          <Select
            isSearchable
            className="ml-2 w-100"
            classNamePrefix="select"
            ref={priceTypeRef}
            defaultValue={actionDropdowns?.priceTypes?.find((item: any) => item.value === actionItem?.priceTypeID)}
            options={actionDropdowns.priceTypes}
            onChange={e =>
              setActionUpdatedValues({ ...actionUpdatedValues, priceTypeID: e?.value, priceTypeName: e?.label })
            }
          />
        </FormLabel>
        <FormLabel className="d-flex items-center">
          <span className="w-40">Unit of Measure</span>
          <Select
            isSearchable
            className="ml-2 w-100"
            classNamePrefix="select"
            ref={unitRef}
            defaultValue={actionDropdowns?.units?.find((item: any) => item.value === actionItem?.commodityUnitId)}
            options={actionDropdowns.units}
            onChange={e =>
              setActionUpdatedValues({ ...actionUpdatedValues, commodityUnitId: e?.value, commodityUnitName: e?.label })
            }
          />
        </FormLabel>
        <FormLabel className="d-flex items-center">
          <span className="w-40">Currency</span>
          <Select
            isSearchable
            className="ml-2 w-100"
            classNamePrefix="select"
            ref={currencyRef}
            defaultValue={actionDropdowns?.currencies?.find((item: any) => item.value === actionItem?.currencyId)}
            options={actionDropdowns.currencies}
            onChange={e =>
              setActionUpdatedValues({ ...actionUpdatedValues, currencyId: e?.value, currencyName: e?.label })
            }
          />
        </FormLabel>
      </Modal>

      <Modal
        open={showVisualData}
        modalHeading="Visual Data Review"
        secondaryButtonText="Close"
        passiveModal
        wide
        onRequestClose={() => setShowVisualData(false)}
      >
        {visualData?.selectData.length > 0 && (
          <ReportVisualDataReview
            visualData={visualData}
            selectReference={selectRef}
            validationData={validationData}
            onObsValueChange={handleObsValueChange}
            onGraphSelectChange={handleGetGraphData}
          />
        )}
      </Modal>

      <Modal
        open={showValidationModal}
        modalHeading={
          !errors.outliers?.length && !errors.duplicates?.length && !errors.invalid?.length ? "" : "Validation errors"
        }
        secondaryButtonText="Close"
        passiveModal
        wide
        onRequestClose={() => setShowValidationModal(false)}
      >
        <div className="max-h-96 overflow-auto">
          {errors.invalid?.length > 0 && (
            <>
              <p>
                <b>{errors.invalid?.length} Invalid Values: </b>
              </p>
              <hr />
              <table className="table-striped border-none min-w-full">
                <tbody>
                  {errors.invalid.map((error: string, index: number) => (
                    <tr key={error + index}>
                      {/* eslint-disable-next-line react/no-danger,jsx-a11y/control-has-associated-label */}
                      <td className="p-3 text-sm" dangerouslySetInnerHTML={{ __html: error }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {errors.duplicates?.length > 0 && (
            <>
              <p>
                <b>{errors.duplicates?.length} Duplicated Series:</b>
              </p>
              <p className="italic">
                Duplicated series will be automatically discarded and not inserted in the database.
              </p>
              <hr />
              <table className="table-striped border-none min-w-full">
                <tbody>
                  {errors.duplicates.map((error: string, index: number) => (
                    <tr key={error + index}>
                      {/* eslint-disable-next-line react/no-danger,jsx-a11y/control-has-associated-label */}
                      <td className="p-3 text-sm" dangerouslySetInnerHTML={{ __html: error }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {errors.outliers?.length > 0 && (
            <>
              <p className="mt-3">
                <b>{errors.outliers?.length} Secondary Outlier Validation: </b>
              </p>
              <hr />
              <table className="table-striped border-none min-w-full">
                <tbody>
                  {errors.outliers.map((error: string, index: number) => (
                    <tr key={error + index}>
                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label,react/no-danger */}
                      <td className="p-3 text-sm" dangerouslySetInnerHTML={{ __html: error }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {!errors.outliers?.length && !errors.duplicates?.length && !errors.invalid?.length && (
            <p>No error! All data is valid.</p>
          )}
        </div>
      </Modal>

      {validationData && validationData?.length > 0 && (
        <div className="mt-2 d-flex">
          {!data?.length && (
            <Button
              className={`
                text-white
                ${
                  status.isValidated && status.error
                    ? "bg-red-600"
                    : status.isValidated
                    ? status.duplicated
                      ? "bg-yellow-400  border-yellow-200"
                      : "bg-green-500  border-green-400"
                    : "blue"
                }
              `}
              action={handleValidate}
              label={
                status.isValidated
                  ? status.error
                    ? "Invalid data"
                    : status.duplicated
                    ? "Valid with duplicated"
                    : "Cool, All valid!"
                  : "Validate"
              }
            />
          )}
          {status.isValidated && (
            <>
              <Button
                className={`!border-current ${status.error ? "bg-red-600 text-white" : "blue"}`}
                action={() => setShowValidationModal(true)}
                label="Validation report"
              />
              {visualData?.selectData.length > 0 && (
                <Button
                  className="blue !border-current"
                  action={() => setShowVisualData(true)}
                  label="Visual data review"
                />
              )}
              {!status.error && (
                <Button
                  className="bg-green-500 border-green-700 text-white"
                  action={handleSave}
                  label="Insert data & save report"
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
